import { useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import {
  Dropdown,
  Menu,
  MenuItem as BaseMenuItem,
  MenuButton as BaseMenuButton,
} from '@mui/base'
import { HelpOutline, KeyboardArrowDown } from '@mui/icons-material'
import {
  CustomTableCell,
  TableHead,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
  AutoCompleteWrapper,
  UserListActionsModal
} from '../../components'
import {
  UserFullListProvider,
  useUserFullList,
} from '../../contexts/UserFullListContext'
import useViewport from '../../hooks/useViewport'
import { getComparator } from '../../utils/graph'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import {
  TYPE,
  UserListActionsModalProvider,
  useUserListActionsModal,
} from '../../contexts/UserListActionsModalContext'
import UserCreateUpdateActionsModal from '../../components/UserCreateUpdateActionsModal'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  background-color: #f1f3f5;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 44px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`


const MenuButton = styled(BaseMenuButton)`
  background-color: #f1f3f5;
  border: 0;
  border-radius: 68px;
  cursor: pointer;
`

const MenuItem = styled(BaseMenuItem)`
  list-style: none;
  padding: 8px;
  cursor: pointer;
  user-select: none;

  &:last-of-type {
    border-bottom: none;
  }

  &:focus {
    outline: 3px solid #e27837;
    background-color: #e27837;
    color: #f1f3f5;
  }
`

const Listbox = styled('ul')(
  ({ theme }) => `
  margin: 8px 0;
  min-width: 200px;
  overflow: auto;
  outline: 0px;
  background: #FFF;
  border: 1px solid #d6d7db;
  color: black;
  box-shadow: 0px 4px 30px #FFF;
  z-index: 1;

  .closed & {
    opacity: 0;
    transform: scale(0.95, 0.8);
    transition: opacity 200ms ease-in, transform 200ms ease-in;
  }

  .open & {
    opacity: 1;
    transform: scale(1, 1);
    transition: opacity 100ms ease-out, transform 100ms cubic-bezier(0.43, 0.29, 0.37, 1.48);
  }

  .placement-top & {
    transform-origin: bottom;
  }

  .placement-bottom & {
    transform-origin: top;
  }
  `,
)

const rowsPerPage = 10

const UserFullListPage = () => {
  const [order, setOrder] = useState('asc')
  const [page, setPage] = useState(0)
  const { width } = useViewport()
  const { openActionsModal } = useUserListActionsModal()
  const { t } = useTranslation('common')

  const headState = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.name'),
      align: 'left',
    },
    {
      id: 'level',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.level'),
      align: 'left',
    },
    {
      id: 'company_designated_id',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.company_designated_id'),
      align: 'left',
    },
    {
      id: 'role',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.role'),
      align: 'left',
    },
    {
      id: 'shift',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.shift'),
      align: 'left',
    },
    {
      id: 'usual_sleep',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.usual_sleep'),
      align: 'left',
    },
    {
      id: 'has_usual_caffeine',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.usual_caffeine'),
      align: 'left',
    },
    {
      id: 'login',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.login'),
      align: 'left',
    },
    {
      id: 'pvt',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.pvt'),
      align: 'left',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: t('list.users.header.status'),
      align: 'left',
    },
  ]
  const [orderBy, setOrderBy] = useState(headState[0].id)

  const {
    rows,
    levelCount,
    loading: loadingData,
    loadingFilters,
    filterOptions,
    onSelectFilter,
    selectedFilterOptions,
    selectedUser,
    onSelectUser,
    inputValue,
    onInputValueChange,
  } = useUserFullList()
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper
  const { type } = useUserListActionsModal()

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  )

  const handleButtonClick = ({ modalType }) => {
    openActionsModal({ type: modalType })
  }

  return (
    <Container>
      <ContentContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {t('list.users.title')}
            </span>
            <Tooltip
              title={t('list.users.tooltip')}
              placement='right'
              arrow
            >
              <HelpOutline fontSize='18' />
            </Tooltip>
          </div>
          <Dropdown>
            <MenuButton>
              <Button
                variant='outlined'
                onClick={() => { }}
                endIcon={<KeyboardArrowDown />}
              >
                {t('list.users.actions.text')}
              </Button>
            </MenuButton>
            <Menu slots={{ listbox: Listbox }}>
              <MenuItem onClick={() => handleButtonClick({ modalType: TYPE.STATUS })}>
                {t('list.users.actions.mass')}
              </MenuItem>
              <MenuItem onClick={() => handleButtonClick({ modalType: TYPE.USER })}>
                {t('list.users.actions.user')}
              </MenuItem>
            </Menu>
          </Dropdown>
        </Header>
        <BodyContainer>
          <FiltersTagsContainer>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'management' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 16,
                  }}
                >
                  <Filter onClick={() => setOpenFilterDrawer(true)} />
                </div>
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            })?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <GroupedFilterTags
                    tags={selectedFilterOptionsList({
                      selectedFilterOptions,
                    })}
                    onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                    maxTagsToShow={getMaxTagsToShow({ width })}
                  />
                </div>
              )}
          </FiltersTagsContainer>
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby='notificationsTable'
            >
              <TableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                headCells={headState}
              />
              <TableBody>
                {!loadingData ? (
                  <>
                    {rows.length > 0 ? (
                      <>
                        {visibleRows.map(row => {
                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={row.name}
                            >
                              <CustomTableCell
                                data={row.name}
                                type='text'
                                sx={{ cursor: 'pointer' }}
                              />
                              <CustomTableCell
                                data={row.role_level}
                                type='text'
                                sx={{ cursor: 'pointer' }}
                              />
                              <CustomTableCell data={row.company_designated_id} />
                              <CustomTableCell data={row.role_name} />
                              <CustomTableCell data={row.shift_name} />
                              <CustomTableCell
                                data={row.usual_sleep}
                                sx={{ whiteSpace: 'nowrap' }}
                              />
                              <CustomTableCell data={t(row.has_usual_caffeine ? 'yes' : 'no')} />
                              <CustomTableCell data={row.login} />
                              <CustomTableCell data={row.last_pvt} />
                              <CustomTableCell data={row.status} />
                            </TableRow>
                          )
                        })}
                        {emptyRows() > 0 && (
                          <TableRow
                            style={{
                              height: 53 * emptyRows(),
                            }}
                          >
                            <TableCell colSpan={headState.length} />
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <TableRow style={{ height: 53 * 10 }}>
                        <TableCell
                          colSpan={headState.length}
                          align='center'
                        >
                          {t('no_data')}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow style={{ height: 53 * 10 }}>
                    <TableCell
                      colSpan={headState.length}
                      align='center'
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={rows.length}
            showFirstButton
            showLastButton
            labelRowsPerPage={''}
            rowsPerPageOptions={[]}
            rowsPerPage={10}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ to, count }) =>
              `${to} de ${count} usuários`
            }
          />
        </BodyContainer>
      </ContentContainer>
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingFilters}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />
      {type === TYPE.STATUS ? <UserListActionsModal /> : <UserCreateUpdateActionsModal />}
    </Container>
  )
}

const UserFullList = () => {
  return (
    <UserListActionsModalProvider>
      <UserFullListProvider>
        <UserFullListPage />
      </UserFullListProvider>
    </UserListActionsModalProvider>
  )
}

export default UserFullList
